import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { AnalyticsCharts } from '../../../ui/analytics_charts/AnalyticsCharts';
import { CollapsibleFilter } from '../../../ui/data_source_filter/CollapsibleFilter';
import { Loader } from '../../../ui/loader/loader';
import { defaultChartOrder } from '../../../utils/analytics_data/chartData';
import { assessmentTypes } from '../../../utils/analytics_data/filterData';
import { formatAPIData } from '../../../utils/analytics_data/formatter';
import { searchAnalytics } from '../../../utils/api_service/analytics_api';
import { getEmployee } from '../../../utils/api_service/employees';
import { getOrganizationTree } from '../../../utils/api_service/organization_unit';
import { getDashletOrder } from '../../../utils/api_service/organization_users';
import { getOrgUserId } from '../../../utils/helpers/auth';
import { RootState } from '../../../utils/redux_store/store';
import { AnalyticsData, AnalyticsSearchObject, AnalyticsSearchRequest, ChartOrderItem } from '../../../utils/types/analytics/analytics_types';
import { SelectOptions } from '../../../utils/types/react_select_types';
import { OrganizationUnit } from '../../admin/OrganizationSettings/TreeStructure';

const CHART_TYPE = 'organization';

const getOrganizationUnit = (organisationUnit: OrganizationUnit, organisationUnitId: string): OrganizationUnit | undefined=> {
  if(organisationUnit.id === organisationUnitId) {
    return organisationUnit;
  }

  for (const org of organisationUnit.children) {
    return getOrganizationUnit(org, organisationUnitId);
  }
};

export const OrganisationAnalytics = () => {

  const skills = useSelector((state: RootState) => state.skills);
  const [skillList, setSkillList] = useState<SelectOptions[]>(skills.skillsList);
  const [searchObject, setSearchObject] = useState<AnalyticsSearchObject>({
    timeRange: { startDate: dayjs().subtract(1, 'year').toDate(), endDate: dayjs().toDate() },
    organization: [],
    assessmentType: [],
    skill: [],
  });
  const [searchResult, setSearchResult] = useState<AnalyticsData>();
  const [loading, setLoading] = useState<boolean>(false);
  const [searchResultChartOrder, setSearchResultChartOrder] = useState<ChartOrderItem[]>();
  const [organizationTree, setOrganizationTree] = useState<OrganizationUnit>();
  const [chartOrder, setChartOrder] = useState<ChartOrderItem[]>(defaultChartOrder);

  useEffect(() => {
    const debounceSearchTerm = setTimeout(() => {
      setLoading(true);

      const searchTerms: AnalyticsSearchRequest = {
        dashboardType: CHART_TYPE,
        timeRange: searchObject.timeRange,
        organization: searchObject.organization.map(item => item.value),
        assessmentType: searchObject.assessmentType.map(item => item.value),
        skill: searchObject.skill.map(item => item.value),
      };
      searchAnalytics(searchTerms).then(async response => {
        const formattedSearchResult: any = await formatAPIData(response);
        setSearchResult(formattedSearchResult);
        setLoading(false);
      });
    }, 1000);

    return () => clearTimeout(debounceSearchTerm);
  }, [searchObject]);

  useEffect(() => {
    if (!chartOrder || !searchResult) return;

    const temp: ChartOrderItem[] = [];
    for (const item of chartOrder) {
      if (Object.hasOwn(searchResult, item.chartId)) {
        temp.push(item);
      }
    }
    setSearchResultChartOrder(temp);
  }, [chartOrder, searchResult]);

  useEffect(() => {
    setSkillList(skills.skillsList);
  }, [skills.skillsList]);

  useEffect(() => {
    const orgUserId = getOrgUserId();
    getEmployee(orgUserId).then(employee => {
      const orgUnitId = employee?.organizationUnitId?.id || null;
      getOrganizationTree().then(response => {
        if(orgUnitId) {
          const org = getOrganizationUnit(response, orgUnitId);
          setOrganizationTree(org);
        } else {
          setOrganizationTree(response);
        }
      });
    });

    setChartOrder(defaultChartOrder);
    getDashletOrder(CHART_TYPE).then(response => {
      if (!response.chartOrder?.length) return;
      setChartOrder(response.chartOrder);
    }).catch( () => {
      setChartOrder(defaultChartOrder);
    });
  }, []);

  return (
    <>
      <Box sx={{ minHeight: '100vh', width: '100%', display: 'flex', flexDirection: 'column', gap: 2.5, pb: 6 }}>
        <Typography variant='h5' sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '-0.02em', my: 1 }}>
          Organisation Analytics
        </Typography>
        <Paper
          variant='outlined'
          sx={{
            display: 'flex',
            flexDirection: 'column',
            bgcolor: '#ffffff',
            p: { xs: 2, sm: 3 },
            borderRadius: '20px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04)',
            borderColor: 'divider',
            gap: 2,
          }}
        >
          <CollapsibleFilter
            open={true}
            skillList={skillList}
            organizationTree={organizationTree}
            assessmentTypeList={assessmentTypes}
            searchObject={searchObject}
            setSearchObject={setSearchObject}
            searchResultChartOrder={searchResultChartOrder}
          />
          <AnalyticsCharts
            searchResult={searchResult}
            chartOrder={chartOrder}
            setChartOrder={setChartOrder}
            searchResultChartOrder={searchResultChartOrder}
            type={CHART_TYPE}
          />
        </Paper>
      </Box>
      <Loader
        loading={loading}
      />
    </>
  );
};

